import { useEffect, useState } from "react";

import {
  getCurrentDateTime,
  getDateDaysAgo,
  getDateMonthsAgo,
  getFirstDayOfCurrentMonth,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  isToday,
} from "@/features/dashboard/utils/date";
import type { DateFilter } from "@/features/reports/schemas/reports";

/**
 * Hook personalizado para manejar filtros de fecha
 * @returns {Object} Objeto con el estado del filtro de fecha y funciones para manejarlo
 */
export function useDateFilter(): {
  dateFilter: DateFilter;
  showCustomDate: boolean;
  handleDateFilterChange: (type: DateFilter["type"]) => void;
  handleCustomDateChange: (field: "startDate" | "endDate", value: string) => void;
  getPeriodText: () => string;
} {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: "today",
  });
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Inicializar las fechas cuando el hook se monta
  useEffect(() => {
    const now = getCurrentDateTime();
    setDateFilter({
      type: "today",
      startDate: now,
      endDate: now,
    });
  }, []);

  /**
   * Manejador para el cambio de filtro de fecha
   * @param {DateFilter["type"]} type - El tipo de filtro de fecha
   */
  const handleDateFilterChange = (type: DateFilter["type"]) => {
    setShowCustomDate(false);
    const now = getCurrentDateTime();

    switch (type) {
      case "today": {
        // Hoy: inicio del día actual hasta la hora actual
        setDateFilter({
          type,
          startDate: now,
          endDate: now, // Hora actual
        });
        break;
      }
      case "lastWeek": {
        // Última semana: hace 7 días desde el inicio del día hasta la hora actual
        const lastWeekStart = getDateDaysAgo(7);
        setDateFilter({
          type,
          startDate: lastWeekStart,
          endDate: now, // Hora actual
        });
        break;
      }
      // Mes actual: primer día del mes desde el inicio del día hasta la hora actual
      case "currentMonth": {
        // Mes actual: primer día del mes desde el inicio del día hasta la hora actual
        const startOfMonth = getFirstDayOfCurrentMonth();
        setDateFilter({
          type,
          startDate: startOfMonth,
          endDate: now, // Hora actual
        });
        break;
      }
      // Mes pasado: primer día del mes pasado hasta el último día del mes pasado (calendario local)
      case "lastMonth": {
        const now = getCurrentDateTime();
        // Usar componentes locales de `now` (nunca Date.UTC con y/m/d locales:
        // en UTC-4 el día 1 a cualquier hora caería en el mes anterior).
        const firstDay = getFirstDayOfMonth(now.getFullYear(), now.getMonth() - 1);
        const lastDay = getLastDayOfMonth(now.getFullYear(), now.getMonth() - 1);
        lastDay.setHours(23, 59, 59, 999);

        setDateFilter({
          type,
          startDate: firstDay,
          endDate: lastDay,
        });
        break;
      }
      case "lastThreeMonths": {
        // Últimos tres meses: hace 3 meses desde el inicio del día hasta la hora actual
        const threeMonthsAgo = getDateMonthsAgo(3);
        setDateFilter({
          type,
          startDate: threeMonthsAgo,
          endDate: now, // Hora actual
        });
        break;
      }
      case "custom": {
        setShowCustomDate(true);
        // Para custom, no establecemos fechas por defecto
        setDateFilter({ type });
        break;
      }
      default: {
        // Por defecto: hoy
        setDateFilter({
          type: "today",
          startDate: now,
          endDate: now,
        });
        break;
      }
    }
  };

  /**
   * Manejador para el cambio de fecha personalizada
   * @param {string} field - El campo a cambiar
   * @param {string} value - El valor del campo
   */
  const handleCustomDateChange = (field: "startDate" | "endDate", value: string) => {
    setDateFilter((prev) => {
      const newDate = new Date(value);

      if (field === "startDate") {
        // Para startDate: establecer inicio del día (00:00:00.000)
        const startOfDay = new Date(newDate);
        return {
          ...prev,
          startDate: startOfDay,
        };
      } else {
        // Para endDate: establecer fin del día (23:59:59.999) o hora actual si es hoy
        const now = getCurrentDateTime();
        const isTodayDate = isToday(newDate);

        let endDate: Date;
        if (isTodayDate) {
          // Si es hoy, usar la hora actual
          endDate = now;
        } else {
          // Si no es hoy, usar fin del día
          endDate = new Date(newDate.setHours(23, 59, 59, 999));
        }

        return {
          ...prev,
          endDate,
        };
      }
    });
  };

  /**
   * Función para obtener el texto del período
   * @returns {string} El texto del período
   */
  const getPeriodText = () => {
    switch (dateFilter.type) {
      case "today":
        return "Hoy";
      case "lastWeek":
        return "Semana pasada";
      case "currentMonth":
        return "Mes actual";
      case "lastMonth":
        return "Mes pasado";
      case "lastThreeMonths":
        return "Últimos tres meses";
      case "custom":
        return "Personalizado";
      default:
        return "Hoy";
    }
  };

  return {
    dateFilter,
    showCustomDate,
    handleDateFilterChange,
    handleCustomDateChange,
    getPeriodText,
  };
}
